/**
 * `last_updated` — avatar of `updater_id` + a relative or absolute date per `dateFormatMode`
 * (M2 §5.10, §6). `updater_id` may be null when an automation wrote the cell.
 */
import { getColumnType, type LastUpdatedSettings, type LastUpdatedValue } from '@elizmonday/shared';
import { RelativeTime } from '@elizmonday/ui';
import { PersonAvatar, UnknownAvatar } from '../primitives';
import { formatLogDate } from '../creation-log/index';
import { defineColumnUi } from '../types';
import { sharedBuckets } from '../buckets';
import { LastUpdatedSettingsDialog } from './SettingsDialog';

const shared = getColumnType('last_updated');

/* Same as `creation_log`: the shared settings type, not a local widening of it (round 10i). */
export const lastUpdatedColumnUi = defineColumnUi<LastUpdatedValue, LastUpdatedSettings>({
  type: 'last_updated',
  icon: 'columnLastUpdated',
  gallery: { group: 'dates', keywords: ['last updated', 'modified', 'changed'] },
  /** P-022: `717364248` `Last Updated` (`width: null`) renders **135** px in monday. */
  defaultWidth: 135,
  align: 'center',
  groupable: false,

  Cell: ({ value, settings, context }) => {
    if (!value) return <span className="ez-cell__empty" aria-hidden="true" />;
    const person = context.people.find((entry) => String(entry.id) === String(value.updater_id));
    return (
      <span className="ez-log-cell">
        {person ? (
          <PersonAvatar name={person.name} color={person.color} size={20} />
        ) : (
          <UnknownAvatar size={20} />
        )}
        {settings.dateFormatMode === 'dateAgo' && value.updated_at ? (
          <RelativeTime value={value.updated_at} />
        ) : (
          formatLogDate(value.updated_at, context.timezone)
        )}
      </span>
    );
  },

  SettingsDialog: LastUpdatedSettingsDialog,

  fromClipboard() {
    throw new Error('read-only');
  },
  toClipboard(value, settings) {
    return shared.toText(value, settings) ?? '';
  },
  bucketsOf: sharedBuckets('last_updated'),
});
